import { useMemo, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { ButtonBase } from '@mui/material';
import { useAtomValue } from 'jotai';

import { VideaTypography } from '../../../../../common-components/elements/VideaTypography/VideaTypography';
import type { Appointment } from '../../../../../react-query/useAppointments';
import { type GetTaskResponse } from '../../../../../react-query/useGetTasks';
import type { Indicator } from '../../PatientStrip';
import { tasksOnAtom } from '../atoms';

import { GamifiedTasksRedesigned } from './gamifiedTasksRedesigned';

const MAX_ITEMS = 6;

export const RecommendedActionsRedesigned = ({
  appointment,
  patientTask,
}: TaskRedesignedProps) => {
  const isTasksOn = useAtomValue(tasksOnAtom);

  const [expanded, setExpanded] = useState<'indicators' | 'tasks'>(
    'indicators',
  );

  const numTasks = useMemo(
    () =>
      (patientTask?.tasks ?? []).filter(
        (task) => task.status !== 'NOT_SUGGESTED',
      ).length,
    [patientTask],
  );
  const showAccordion = numTasks > MAX_ITEMS;

  const displayTasks = (
    <GamifiedTasksRedesigned
      appointment={appointment}
      context="PATIENT_STRIP"
      patientTask={patientTask}
    />
  );

  if (!showAccordion) {
    return (
      <div className="vai-recommended-actions vai-flex vai-flex-col vai-items-start">
        {isTasksOn && displayTasks}
      </div>
    );
  }

  return (
    <div className="vai-flex vai-flex-col vai-gap-4">
      {expanded === 'tasks' && displayTasks}
      {expanded !== 'tasks' && !!numTasks && (
        <>
          <div className="vai-circle-border-text vai-flex vai-items-center vai-justify-center vai-bg-blue-100 vai-text-blue-800 vai-h-8 vai-w-8 vai-rounded-full vai-font-bold">
            {numTasks}
          </div>
          <VideaTypography
            variant="bodyExtrasmall"
            className="vai-tasks-due vai-text-sm vai-text-gray-600"
          >
            <Trans>Tasks due</Trans>
          </VideaTypography>
          <ButtonBase
            className="vai-text-button vai-text-blue-600 hover:vai-text-blue-800 vai-transition vai-duration-200"
            onClick={() => setExpanded('tasks')}
          >
            <VideaTypography
              variant="bodyExtrasmall"
              className="vai-interactable vai-underline vai-text-sm vai-font-medium"
            >
              <Trans>See All</Trans>
            </VideaTypography>
          </ButtonBase>
        </>
      )}
    </div>
  );
};

interface TaskRedesignedProps {
  appointment: Appointment;
  indicators?: Indicator[] | undefined;
  patientTask?: GetTaskResponse;
}
